/**
 * Bulk check-in body: collapsible Settings + File(s) list with expandable rows.
 *
 * Single-active model: Settings OR one file row is "active" at a time and bound to the shared
 * Redux check-in slice via `DocumentBasicItem`. Each section persists its own draft locally.
 *
 * Behavior:
 *  1) Settings Class/Format propagates to every file that hasn't been individually overridden.
 *  2) Editing Class/Format inside a file row updates only that file (it becomes "overridden")
 *     and never writes back to Settings.
 *  3) View-copy PDF uploads in a file row apply only to that file (stored per draft).
 *  4) Settings "Delete OneDrive original" propagates to every file unless that file was
 *     individually overridden (`oneDriveDeleteOverridden`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DefaultButton,
  Icon,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TaskListSquareSettingsRegular } from "@fluentui/react-icons";
import { useLocation, useNavigate } from "react-router-dom";

import DocumentBasicItem from "../../components/checkinDocuments/DocumentBasicItem";
import DeleteOneDriveCheckbox from "../check-in-new-revision/components/DeleteOneDriveCheckbox";
import ReusableDialog from "../../components/popupModals/ReusableDialog";
import MessageBoxContent from "../../components/popupModals/MessageBoxContent";
import { APP_ROUTES } from "../../routes/routeConfig";
import { useTheme } from "../../theme/OAS365Theme";
import { useCommonControlsStyle } from "../../theme/styles/CommonControlsStyle";
import { logger } from "../../utils/logger";
import { useAppDispatch } from "../../redux/hooks";
import {
  ON_ERROR_MESSAGE_INVOKE,
  ON_SUCCESS_MESSAGE_INVOKE,
} from "../../redux/constants/actionTypes";
import { ERROR_TYPE, SUCCESS_TYPE } from "../../redux/constants/messageTypes";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useCheckinDocument } from "../../redux/hooks/useCheckinDocument";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useTools } from "../../redux/hooks/useTools";
import { useLogin } from "../../redux/hooks/useLogin";
import { useOneDrive } from "../../redux/hooks/useOneDrive";
import { checkinDocumentsBulkThunk } from "../../redux/slices/checkinApi";
import {
  buildBulkDocInfo,
  clearBulkCheckinState,
  cloneViewCopyFiles,
  draftHasManualViewCopyAttachment,
  buildBulkOneDriveServerContext,
  collectBulkOneDriveDeleteIds,
  formatBulkCheckinToastMessage,
  getBulkCheckinToastTitle,
  getBulkOneDriveFileUrlFromDoc,
  isViewCopyRequirementMet,
  getEffectiveViewCopyUploadCount,
  resolveBulkViewRequiredFlag,
  mergeActiveFileViewCopyIntoDraftMap,
  mergeFileViewCopyIntoDraft,
  resolveOneDriveDeleteRequired,
} from "./checkInBulk.utils";
import { resolveGroupId } from "../check-in-new-list/checkInNewList.utils";
import {
  DATA_LOAD_STATUS,
  DOC_CHECK_IN_TYPE,
  DOCUMENT_LOAD_STATUS,
  REFRESH_TOKEN_ACTION,
} from "../../redux/constants/metaData";
import { TAB_KEY, CHECKIN_LOADING_MESSAGE } from "../check-in-new/components/checkInNew.constants";
import {
  isCheckinInLoadingState,
  shouldTriggerRefreshTokenPreAction,
} from "../check-in-new/components/checkInNew.helpers";
import { getFileName, getDocClassCodeOnly } from "../../functions/helperMethod";

/** Dev: surface `DefaultButton` binding (footer uses `PrimaryButton` only) — avoids ReferenceError if HMR serves a mixed chunk. */
void DefaultButton;

/** @typedef {{ key?: string, name?: string, title?: string, iconName?: string, downloadlink?: string, fileType?: string }} ListDocRow */
/** @typedef {{ docClass: string, docFormat: string, docTitle: string, overridden: boolean, viewRequired?: string, formatRequired?: string, objConRequired?: string, createViewCopy?: boolean, docReleaseStatus?: string, viewCopyOverridden?: boolean, releaseOverridden?: boolean, oneDriveDeleteRequired?: boolean, oneDriveDeleteOverridden?: boolean, objectConnections?: Array<any>, objectConnectionsOverridden?: boolean, viewCopyFiles?: Array<any>, uploadViewCopyCount?: number }} FileDraft */

const SETTINGS_KEY = "settings";
/** No file or settings section is actively mirrored into Redux. */
const INACTIVE_KEY = "inactive";

const BULK_ACC_HEADER_SUMMARY_SX = {
  minHeight: "56px !important",
  "&.Mui-expanded": { minHeight: "56px !important" },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: "12px 0 !important",
  },
};

const BULK_ACC_CARD_SURFACE_BG = "#f4fbf6";

const isApiFlagYes = (value) => value === true || value === "Y";
const normalizeObjectConnections = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      luName: item?.luName ?? "",
      keyRef: item?.keyRef ?? "",
      selectType: item?.selectType ?? "",
    }))
    .sort((a, b) =>
      `${a.luName}|${a.keyRef}|${a.selectType}`.localeCompare(
        `${b.luName}|${b.keyRef}|${b.selectType}`,
      ),
    );
const areObjectConnectionsEqual = (a, b) =>
  JSON.stringify(normalizeObjectConnections(a)) ===
  JSON.stringify(normalizeObjectConnections(b));

const areViewCopyFilesEqual = (a, b) => {
  const arrA = Array.isArray(a) ? a : [];
  const arrB = Array.isArray(b) ? b : [];
  if (arrA.length !== arrB.length) {
    return false;
  }
  return arrA.every(
    (file, index) =>
      (file?.name ?? "") === (arrB[index]?.name ?? "") &&
      (file?.base64str?.length ?? 0) === (arrB[index]?.base64str?.length ?? 0),
  );
};

function TooltipWarningIcon({ incomplete }) {
  if (!incomplete) {
    return null;
  }
  const tip =
    "Complete all required fields in Settings and for this file before check-in.";
  return (
    <Tooltip title={tip} arrow placement="top">
      <span style={{ display: "inline-flex" }}>
        <Icon
          iconName="Warning"
          ariaLabel={tip}
          styles={{
            root: {
              flexShrink: 0,
              color: "#eab308",
              fontSize: 18,
              height: 18,
              width: 18,
            },
          }}
        />
      </span>
    </Tooltip>
  );
}

function BulkCheckInFileRow({
  doc,
  expanded,
  incomplete,
  onToggleExpand,
  onTreeDataLoadingChange,
  objectConnectionTabKey,
  onObjectConnectionTabKeyChange,
  tabKey,
  oneDriveDeleteChecked,
  onOneDriveDeleteChange,
}) {
  const theme = useTheme();
  const docLabel = doc.name ?? doc.title ?? doc.key ?? "Document";

 // console.log("docLabel:", docLabel, "incomplete:", incomplete);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, exp) => onToggleExpand(doc.key, exp)}
      disableGutters
      sx={{
        mb: 1.25,
        boxShadow: "none !important",
        borderRadius: "8px",
        border: `1px solid ${theme.colors.addovation20Green ?? "#b7d9c8"}`,
        "&:before": { display: "none" },
        backgroundColor: BULK_ACC_CARD_SURFACE_BG,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
        aria-controls={`bulk-file-${doc.key}-content`}
        id={`bulk-file-${doc.key}-header`}
        sx={{
          ...BULK_ACC_HEADER_SUMMARY_SX,
          px: 1,
          "& .MuiAccordionSummary-content": {
            ...BULK_ACC_HEADER_SUMMARY_SX["& .MuiAccordionSummary-content"],
            gap: "12px",
          },
        }}
      >
        <img
          src={doc.iconName}
          alt=""
          width={18}
          height={18}
          style={{ flexShrink: 0 }}
          onError={(e) => {
            e.target.style.visibility = "hidden";
          }}
        />
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "#2d6e4f",
            fontFamily: theme.fonts.regular,
            fontWeight: 600,
            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
          }}
          title={docLabel}
        >
          {docLabel}
        </Typography>
        <TooltipWarningIcon incomplete={incomplete} />
      </AccordionSummary>
      <AccordionDetails
        sx={{
          pt: 0,
          pb: 2,
          px: 2,
          backgroundColor: theme.colors.backgroundColor,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {expanded && (
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <DocumentBasicItem
              key={`file:${doc.key}`}
              omitOneDriveDeletionRow
              onTreeDataLoadingChange={onTreeDataLoadingChange}
              objectConnectionTabKey={objectConnectionTabKey}
              onObjectConnectionTabKeyChange={onObjectConnectionTabKeyChange}
              tabKey={tabKey}
            />
            <DeleteOneDriveCheckbox
              checked={oneDriveDeleteChecked}
              onChange={onOneDriveDeleteChange}
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function CheckInNewBulkContent() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();
  const tabKey = TAB_KEY;

  /** @type {{ documents?: ListDocRow[], from?: string }} */
  const state = location.state ?? {};

  const documents = useMemo(() => {
    const docs = state.documents;
    return Array.isArray(docs) ? docs : [];
  }, [state.documents]);

  /** `'settings'` | `'file:<key>'` — which section is mirrored into Redux. */
  const [activeKey, setActiveKey] = useState(SETTINGS_KEY);
  /** Visual expand/collapse for Settings; independent of `activeKey` so collapse does not unmount. */
  const [settingsPanelExpanded, setSettingsPanelExpanded] = useState(true);
  const [objectConnectionTabKey, setObjectConnectionTabKey] = useState(
    tabKey.checkinNewSelectedObj,
  );
  const [, setIsTreeDataLoading] = useState(false);

  /** Settings has no Title; each file has its own Title (and an `overridden` flag). */
  const [settingsDraft, setSettingsDraft] = useState({
    docClass: "",
    docFormat: "",
    viewRequired: "N",
    formatRequired: "N",
    objConRequired: "N",
    createViewCopy: false,
    docReleaseStatus: "",
    oneDriveDeleteRequired: true,
    objectConnections: [],
  });
  /** @type {[Record<string, FileDraft>, Function]} */
  const [draftByKey, setDraftByKey] = useState({});

  const {
    docTitle,
    docClass,
    docFormat,
    formatRequired,
    viewRequired,
    uploadViewCopyCount,
    createViewCopy,
    objConRequired,
    selectedObjectConnections,
    docReleaseStatus,
    viewCopyFiles,
    docClassFormatList,
    checkinDocumentProgress,
    onObjectConnectionsLoad,
    getRecentObjects,
  } = useCheckin();
  const {
    objectConnectionDataLoad,
    docCheckinType,
    tokenRefreshPreActionInvoke,
  } = useMetadata();
  const {
    onFileUploadChange,
    onFileDataChange,
    onDocClassFormatChange,
    onDocTitleChange,
    onViewCopyUploadChange,
    onDocReleaseChange,
    onCreateViewCopyDisable,
    onCreateViewCopySelect,
    onObjectConnectionDataLoad,
    onDocClassFormatListChange,
    onRecentObjectConnectionChanged,
    onSelectedObjectConnectionChanged,
    onSelectedKeyLuNameChange,
    onRecentObjectSelectedDescription,
    onRecentObjectDataLoadStatusChanged,
    onAllowSelectionChange,
    onCheckinDocumentProgress,
    onOneDriveDataLoad,
    onDocCheckinTypeChange,
  } = useTools();
  const { teamsContext } = useLogin();
  const { deleteOneDriveDocumentsBulk } = useOneDrive();
  const { checkinBulkLoading } = useCheckinDocument();

  // Refs let callbacks read latest state without becoming new each render.
  const activeKeyRef = useRef(activeKey);
  const settingsDraftRef = useRef(settingsDraft);
  const draftByKeyRef = useRef(draftByKey);
  const documentsRef = useRef(documents);
  useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);
  useEffect(() => {
    settingsDraftRef.current = settingsDraft;
  }, [settingsDraft]);
  useEffect(() => {
    draftByKeyRef.current = draftByKey;
  }, [draftByKey]);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  /** Guards for the Redux ↔ draft sync loop. */
  const lastSeenReduxRef = useRef({
    docClass: docClass ?? "",
    docFormat: docFormat ?? "",
    docTitle: typeof docTitle === "string" ? docTitle : "",
    createViewCopy: createViewCopy === true,
    docReleaseStatus: docReleaseStatus ?? "",
  });
  /** When we hydrate Redux from a draft, the next Redux update should NOT be saved back. */
  const expectedHydrationRef = useRef(null);
  const lastSeenObjectConnectionsRef = useRef(
    Array.isArray(selectedObjectConnections) ? selectedObjectConnections : [],
  );
  const expectedObjectConnectionsRef = useRef(null);
  const lastSeenViewCopyRef = useRef({
    files: cloneViewCopyFiles(viewCopyFiles),
    count: uploadViewCopyCount ?? 0,
  });
  const expectedViewCopyHydrationRef = useRef(null);
  const prevActiveKeyForHydrationRef = useRef(activeKey);

  useEffect(() => {
    if (documents.length) {
      logger.info(
        "[CheckInNewBulkContent] Loaded documents",
        { count: documents.length },
        "ROUTER",
      );
      return;
    }
    navigate(APP_ROUTES.checkInNew, { replace: true });
  }, [documents.length, navigate]);

  /** Refs avoid tying unmount cleanup to Redux values (would re-run cleanup in a loop). */
  const docClassFormatListRef = useRef(docClassFormatList);
  const selectedObjectConnectionsRef = useRef(selectedObjectConnections);
  useEffect(() => {
    docClassFormatListRef.current = docClassFormatList;
  }, [docClassFormatList]);
  useEffect(() => {
    selectedObjectConnectionsRef.current = selectedObjectConnections;
  }, [selectedObjectConnections]);

  /** Reset shared check-in Redux so class/format/obj-con do not leak into single check-in. */
  const resetSharedCheckinState = useCallback(() => {
    clearBulkCheckinState({
      onDocTitleChange,
      onDocClassFormatChange,
      onFileUploadChange,
      onViewCopyUploadChange,
      onDocReleaseChange,
      onCreateViewCopyDisable,
      onCreateViewCopySelect,
      onObjectConnectionDataLoad,
      onDocClassFormatListChange,
      onRecentObjectConnectionChanged,
      onSelectedObjectConnectionChanged,
      onSelectedKeyLuNameChange,
      onRecentObjectSelectedDescription,
      onRecentObjectDataLoadStatusChanged,
      docClassFormatList: docClassFormatListRef.current,
      selectedObjectConnections: selectedObjectConnectionsRef.current,
    });
  }, [
    onCreateViewCopyDisable,
    onCreateViewCopySelect,
    onDocClassFormatChange,
    onDocClassFormatListChange,
    onDocReleaseChange,
    onDocTitleChange,
    onFileUploadChange,
    onObjectConnectionDataLoad,
    onRecentObjectConnectionChanged,
    onRecentObjectDataLoadStatusChanged,
    onRecentObjectSelectedDescription,
    onSelectedKeyLuNameChange,
    onSelectedObjectConnectionChanged,
    onViewCopyUploadChange,
  ]);

  const resetSharedCheckinStateRef = useRef(resetSharedCheckinState);
  useEffect(() => {
    resetSharedCheckinStateRef.current = resetSharedCheckinState;
  }, [resetSharedCheckinState]);

  useEffect(
    () => () => {
      resetSharedCheckinStateRef.current();
    },
    [],
  );

  /** Retry logical-units / favorites loads after IFS token refresh (same as CheckInNew). */
  useEffect(() => {
    if (!shouldTriggerRefreshTokenPreAction(tokenRefreshPreActionInvoke)) {
      return;
    }

    switch (tokenRefreshPreActionInvoke.actionType) {
      case REFRESH_TOKEN_ACTION.logicalUnitsLoad:
        if (teamsContext) {
          onObjectConnectionsLoad(teamsContext, {
            retry: tokenRefreshPreActionInvoke.retry,
            actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
            refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
          });
        }
        break;
      case REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad:
        getRecentObjects({
          retry: tokenRefreshPreActionInvoke.retry,
          actionType: REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad,
          refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
        });
        break;
      default:
        break;
    }
  }, [
    getRecentObjects,
    onObjectConnectionsLoad,
    teamsContext,
    tokenRefreshPreActionInvoke,
  ]);

  /** Seed per-file drafts when documents arrive. Title defaults to file name (no ext). */
  useEffect(() => {
    setDraftByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const doc of documents) {
        const id = String(doc.key);
        if (!next[id]) {
          const fileName = doc?.name ?? doc?.title ?? "";
          next[id] = {
            docClass: "",
            docFormat: "",
            docTitle: fileName ? getFileName(fileName) : "",
            overridden: false,
            viewRequired: "N",
            formatRequired: "N",
            objConRequired: "N",
            createViewCopy: false,
            docReleaseStatus: "",
            viewCopyOverridden: false,
            releaseOverridden: false,
            oneDriveDeleteRequired:
              settingsDraftRef.current.oneDriveDeleteRequired === true,
            oneDriveDeleteOverridden: false,
            objectConnections: [],
            objectConnectionsOverridden: false,
            viewCopyFiles: [],
            uploadViewCopyCount: 0,
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [documents]);

  /** Hydrate Redux from the active section's draft (runs whenever `activeKey` changes). */
  useEffect(() => {
    const previousKey = prevActiveKeyForHydrationRef.current;
    if (previousKey?.startsWith("file:") && previousKey !== activeKey) {
      const outgoingFileKey = previousKey.slice(5);
      const outgoingDraft = draftByKeyRef.current[outgoingFileKey] ?? {};
      const mergedOutgoing = mergeFileViewCopyIntoDraft(
        outgoingDraft,
        lastSeenViewCopyRef.current.files,
        lastSeenViewCopyRef.current.count,
      );
      if (
        mergedOutgoing.viewCopyFiles !== outgoingDraft.viewCopyFiles ||
        mergedOutgoing.uploadViewCopyCount !== outgoingDraft.uploadViewCopyCount
      ) {
        const nextDraftMap = {
          ...draftByKeyRef.current,
          [outgoingFileKey]: mergedOutgoing,
        };
        draftByKeyRef.current = nextDraftMap;
        setDraftByKey(nextDraftMap);
      }
    }
    prevActiveKeyForHydrationRef.current = activeKey;

    let targetClass = "";
    let targetFormat = "";
    let targetTitle = "";
    let targetFileName = "";

    if (activeKey === SETTINGS_KEY) {
      const settings = settingsDraftRef.current;
      targetClass = settings.docClass ?? "";
      targetFormat = settings.docFormat ?? "";
      targetTitle = "";
    } else if (activeKey && activeKey.startsWith("file:")) {
      const fileKey = activeKey.slice(5);
      const draft = draftByKeyRef.current[fileKey] ?? {};
      const doc = documentsRef.current.find((d) => String(d.key) === fileKey);
      targetFileName = doc?.name ?? doc?.title ?? "";
      targetClass = draft.docClass ?? "";
      targetFormat = draft.docFormat ?? "";
      targetTitle =
        draft.docTitle ?? (targetFileName ? getFileName(targetFileName) : "");
    } else {
      return;
    }

    const viewReqFlag =
      activeKey === SETTINGS_KEY
        ? settingsDraftRef.current.viewRequired ?? "N"
        : viewRequired === true || viewRequired === "Y"
          ? "Y"
          : "N";
    const formatReqFlag =
      activeKey === SETTINGS_KEY
        ? settingsDraftRef.current.formatRequired ?? "N"
        : formatRequired === true || formatRequired === "Y"
          ? "Y"
          : "N";
    const objReqFlag =
      activeKey === SETTINGS_KEY
        ? settingsDraftRef.current.objConRequired ?? "N"
        : objConRequired === true || objConRequired === "Y"
          ? "Y"
          : "N";

    const uploadPayload = targetFileName ? [{ name: targetFileName }] : [];
    const targetCreateViewCopy =
      activeKey === SETTINGS_KEY
        ? settingsDraftRef.current.createViewCopy === true
        : (() => {
            const fileKey = activeKey.slice(5);
            const draft = draftByKeyRef.current[fileKey] ?? {};
            return draft.viewCopyOverridden
              ? draft.createViewCopy === true
              : settingsDraftRef.current.createViewCopy === true;
          })();
    const targetDocReleaseStatus =
      activeKey === SETTINGS_KEY
        ? settingsDraftRef.current.docReleaseStatus ?? ""
        : (() => {
            const fileKey = activeKey.slice(5);
            const draft = draftByKeyRef.current[fileKey] ?? {};
            return draft.releaseOverridden
              ? draft.docReleaseStatus ?? ""
              : settingsDraftRef.current.docReleaseStatus ?? "";
          })();
    const targetObjectConnections =
      activeKey === SETTINGS_KEY
        ? settingsDraftRef.current.objectConnections ?? []
        : (() => {
            const fileKey = activeKey.slice(5);
            const draft = draftByKeyRef.current[fileKey] ?? {};
            return draft.objectConnectionsOverridden
              ? draft.objectConnections ?? []
              : settingsDraftRef.current.objectConnections ?? [];
          })();
    const targetViewCopyFiles =
      activeKey === SETTINGS_KEY
        ? []
        : cloneViewCopyFiles(
            draftByKeyRef.current[activeKey.slice(5)]?.viewCopyFiles ?? [],
          );
    const targetUploadViewCopyCount =
      activeKey === SETTINGS_KEY
        ? 0
        : draftByKeyRef.current[activeKey.slice(5)]?.uploadViewCopyCount ?? 0;

    expectedHydrationRef.current = {
      docClass: targetClass,
      docFormat: targetFormat,
      docTitle: targetTitle,
      createViewCopy: targetCreateViewCopy,
      docReleaseStatus: targetDocReleaseStatus,
    };
    expectedObjectConnectionsRef.current = targetObjectConnections;
    expectedViewCopyHydrationRef.current = {
      files: targetViewCopyFiles,
      count: targetUploadViewCopyCount,
    };

    onDocClassFormatChange(
      targetClass,
      targetFormat,
      viewReqFlag,
      formatReqFlag,
      objReqFlag,
      uploadPayload,
      targetUploadViewCopyCount,
    );
    onDocTitleChange(targetTitle);
    if (targetFileName) {
      onFileUploadChange(
        uploadPayload,
        1,
        targetUploadViewCopyCount,
        viewReqFlag === "Y",
      );
    } else {
      onFileDataChange([], 0);
    }
    onDocReleaseChange(targetDocReleaseStatus === "Released");
    onSelectedObjectConnectionChanged(targetObjectConnections, []);
    const viewRequiredFlag =
      viewReqFlag === "Y" || viewRequired === true || viewRequired === "Y";
    onViewCopyUploadChange(
      targetViewCopyFiles,
      targetUploadViewCopyCount,
      targetUploadViewCopyCount,
      viewRequiredFlag,
      uploadPayload,
    );
    onCreateViewCopySelect(targetCreateViewCopy, { programmatic: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on `activeKey`; drafts via refs
  }, [activeKey]);

  useEffect(() => {
    const currentObjectConnections = Array.isArray(selectedObjectConnections)
      ? selectedObjectConnections
      : [];
    if (
      areObjectConnectionsEqual(
        lastSeenObjectConnectionsRef.current,
        currentObjectConnections,
      )
    ) {
      return;
    }

    const expected = expectedObjectConnectionsRef.current;
    if (
      expected &&
      areObjectConnectionsEqual(expected, currentObjectConnections)
    ) {
      lastSeenObjectConnectionsRef.current = currentObjectConnections;
      expectedObjectConnectionsRef.current = null;
      return;
    }

    lastSeenObjectConnectionsRef.current = currentObjectConnections;

    const scope = activeKeyRef.current;
    if (!scope) return;

    if (scope === SETTINGS_KEY) {
      setSettingsDraft((prev) =>
        areObjectConnectionsEqual(prev.objectConnections, currentObjectConnections)
          ? prev
          : { ...prev, objectConnections: currentObjectConnections },
      );
      setDraftByKey((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const id of Object.keys(prev)) {
          const row = prev[id];
          if (row.objectConnectionsOverridden) {
            continue;
          }
          if (!areObjectConnectionsEqual(row.objectConnections, currentObjectConnections)) {
            changed = true;
            next[id] = {
              ...row,
              objectConnections: currentObjectConnections,
            };
          }
        }
        return changed ? next : prev;
      });
      return;
    }

    if (scope.startsWith("file:")) {
      const fileKey = scope.slice(5);
      setDraftByKey((prev) => {
        const row = prev[fileKey] ?? {};
        const merged = {
          ...row,
          objectConnections: currentObjectConnections,
          objectConnectionsOverridden: true,
        };
        if (
          areObjectConnectionsEqual(row.objectConnections, merged.objectConnections) &&
          row.objectConnectionsOverridden === true
        ) {
          return prev;
        }
        return { ...prev, [fileKey]: merged };
      });
    }
  }, [selectedObjectConnections]);

  /** Save Redux view-copy uploads into the active file draft only. */
  useEffect(() => {
    const currentFiles = cloneViewCopyFiles(viewCopyFiles);
    const currentCount = uploadViewCopyCount ?? 0;
    if (
      areViewCopyFilesEqual(lastSeenViewCopyRef.current.files, currentFiles) &&
      lastSeenViewCopyRef.current.count === currentCount
    ) {
      return;
    }

    const expected = expectedViewCopyHydrationRef.current;
    if (
      expected &&
      areViewCopyFilesEqual(expected.files, currentFiles) &&
      expected.count === currentCount
    ) {
      lastSeenViewCopyRef.current = { files: currentFiles, count: currentCount };
      expectedViewCopyHydrationRef.current = null;
      return;
    }

    lastSeenViewCopyRef.current = { files: currentFiles, count: currentCount };

    const scope = activeKeyRef.current;
    if (!scope || !scope.startsWith("file:")) {
      return;
    }

    const fileKey = scope.slice(5);
    setDraftByKey((prev) => {
      const row = prev[fileKey] ?? {};
      const merged = mergeFileViewCopyIntoDraft(
        row,
        currentFiles,
        currentCount,
      );
      if (
        areViewCopyFilesEqual(row.viewCopyFiles, merged.viewCopyFiles) &&
        (row.uploadViewCopyCount ?? 0) === merged.uploadViewCopyCount
      ) {
        return prev;
      }
      return { ...prev, [fileKey]: merged };
    });
  }, [uploadViewCopyCount, viewCopyFiles]);

  /** Save Redux class/format/title back into the active section's draft. */
  useEffect(() => {
    const newClass = docClass ?? "";
    const newFormat = docFormat ?? "";
    const newTitle = typeof docTitle === "string" ? docTitle : "";
    const newCreateViewCopy = createViewCopy === true;
    const newDocReleaseStatus = docReleaseStatus ?? "";

    if (
      lastSeenReduxRef.current.docClass === newClass &&
      lastSeenReduxRef.current.docFormat === newFormat &&
      lastSeenReduxRef.current.docTitle === newTitle &&
      lastSeenReduxRef.current.createViewCopy === newCreateViewCopy &&
      lastSeenReduxRef.current.docReleaseStatus === newDocReleaseStatus
    ) {
      return;
    }

    const expected = expectedHydrationRef.current;
    if (
      expected &&
      expected.docClass === newClass &&
      expected.docFormat === newFormat &&
      expected.docTitle === newTitle &&
      expected.createViewCopy === newCreateViewCopy &&
      expected.docReleaseStatus === newDocReleaseStatus
    ) {
      lastSeenReduxRef.current = {
        docClass: newClass,
        docFormat: newFormat,
        docTitle: newTitle,
        createViewCopy: newCreateViewCopy,
        docReleaseStatus: newDocReleaseStatus,
      };
      expectedHydrationRef.current = null;
      return;
    }

    lastSeenReduxRef.current = {
      docClass: newClass,
      docFormat: newFormat,
      docTitle: newTitle,
      createViewCopy: newCreateViewCopy,
      docReleaseStatus: newDocReleaseStatus,
    };

    const scope = activeKeyRef.current;
    if (!scope) return;

    if (scope === SETTINGS_KEY) {
      const viewReq = viewRequired === true || viewRequired === "Y" ? "Y" : "N";
      const formatReq =
        formatRequired === true || formatRequired === "Y" ? "Y" : "N";
      const objReq = objConRequired === true || objConRequired === "Y" ? "Y" : "N";
      setSettingsDraft((prev) =>
        prev.docClass === newClass &&
        prev.docFormat === newFormat &&
        prev.viewRequired === viewReq &&
        prev.formatRequired === formatReq &&
        prev.objConRequired === objReq &&
        prev.createViewCopy === (createViewCopy === true) &&
        prev.docReleaseStatus === (docReleaseStatus ?? "")
          ? prev
          : {
              docClass: newClass,
              docFormat: newFormat,
              viewRequired: viewReq,
              formatRequired: formatReq,
              objConRequired: objReq,
              createViewCopy: createViewCopy === true,
              docReleaseStatus: docReleaseStatus ?? "",
              oneDriveDeleteRequired: prev.oneDriveDeleteRequired,
              objectConnections: prev.objectConnections ?? [],
            },
      );
      setDraftByKey((prev) => {
        const settingsOneDriveDelete =
          settingsDraftRef.current.oneDriveDeleteRequired === true;
        let changed = false;
        const next = { ...prev };
        for (const id of Object.keys(prev)) {
          const row = prev[id];
          if (
            !row.overridden &&
            (row.docClass !== newClass || row.docFormat !== newFormat)
          ) {
            changed = true;
            next[id] = {
              ...row,
              docClass: newClass,
              docFormat: newFormat,
              viewRequired: viewReq,
              formatRequired: formatReq,
              objConRequired: objReq,
              ...(row.viewCopyOverridden
                ? {}
                : { createViewCopy: createViewCopy === true }),
              ...(row.releaseOverridden
                ? {}
                : { docReleaseStatus: docReleaseStatus ?? "" }),
              ...(row.oneDriveDeleteOverridden
                ? {}
                : { oneDriveDeleteRequired: settingsOneDriveDelete }),
            };
          } else if (
            !row.viewCopyOverridden &&
            row.createViewCopy !== (createViewCopy === true)
          ) {
            changed = true;
            next[id] = { ...next[id], createViewCopy: createViewCopy === true };
          } else if (
            !row.releaseOverridden &&
            row.docReleaseStatus !== (docReleaseStatus ?? "")
          ) {
            changed = true;
            next[id] = { ...next[id], docReleaseStatus: docReleaseStatus ?? "" };
          } else if (
            !row.oneDriveDeleteOverridden &&
            row.oneDriveDeleteRequired !== settingsOneDriveDelete
          ) {
            changed = true;
            next[id] = {
              ...next[id],
              oneDriveDeleteRequired: settingsOneDriveDelete,
            };
          } else if (
            !row.overridden &&
            (row.viewRequired !== viewReq ||
              row.formatRequired !== formatReq ||
              row.objConRequired !== objReq)
          ) {
            changed = true;
            next[id] = {
              ...row,
              viewRequired: viewReq,
              formatRequired: formatReq,
              objConRequired: objReq,
            };
          }
        }
        return changed ? next : prev;
      });
      return;
    }

    if (scope.startsWith("file:")) {
      const fileKey = scope.slice(5);
      const viewReq = viewRequired === true || viewRequired === "Y" ? "Y" : "N";
      const formatReq =
        formatRequired === true || formatRequired === "Y" ? "Y" : "N";
      const objReq = objConRequired === true || objConRequired === "Y" ? "Y" : "N";
      setDraftByKey((prev) => {
        const row = prev[fileKey] ?? {};
        const settingsOneDriveDelete =
          settingsDraftRef.current.oneDriveDeleteRequired === true;
        const merged = {
          ...row,
          docClass: newClass,
          docFormat: newFormat,
          docTitle: newTitle || row.docTitle || "",
          overridden: true,
          viewRequired: viewReq,
          formatRequired: formatReq,
          objConRequired: objReq,
          createViewCopy: createViewCopy === true,
          docReleaseStatus: docReleaseStatus ?? "",
          oneDriveDeleteRequired: row.oneDriveDeleteOverridden
            ? row.oneDriveDeleteRequired === true
            : settingsOneDriveDelete,
          oneDriveDeleteOverridden: row.oneDriveDeleteOverridden === true,
          viewCopyOverridden: true,
          releaseOverridden: true,
        };
        if (
          row.docClass === merged.docClass &&
          row.docFormat === merged.docFormat &&
          row.docTitle === merged.docTitle &&
          row.overridden === true &&
          row.viewRequired === merged.viewRequired &&
          row.formatRequired === merged.formatRequired &&
          row.objConRequired === merged.objConRequired &&
          row.createViewCopy === merged.createViewCopy &&
          row.docReleaseStatus === merged.docReleaseStatus &&
          row.oneDriveDeleteRequired === merged.oneDriveDeleteRequired &&
          row.oneDriveDeleteOverridden === merged.oneDriveDeleteOverridden &&
          row.viewCopyOverridden === true &&
          row.releaseOverridden === true
        ) {
          return prev;
        }
        return { ...prev, [fileKey]: merged };
      });
    }
  }, [
    createViewCopy,
    docClass,
    docFormat,
    docReleaseStatus,
    docTitle,
    formatRequired,
    objConRequired,
    viewRequired,
  ]);

  const fromRoute = state.from ?? APP_ROUTES.checkInNew;

  const finalizeBulkCheckinSuccess = useCallback(() => {
    onAllowSelectionChange(false);
    onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
    onOneDriveDataLoad(DATA_LOAD_STATUS.onLoad);
    resetSharedCheckinState();
    navigate(APP_ROUTES.allDocuments);
  }, [
    navigate,
    onAllowSelectionChange,
    onCheckinDocumentProgress,
    onOneDriveDataLoad,
    resetSharedCheckinState,
  ]);

  const showCheckinProgressDialog =
    checkinBulkLoading ||
    isCheckinInLoadingState(docCheckinType, checkinDocumentProgress);

  const accordionSettingsOuter = useMemo(
    () => ({
      mb: "16px",
      borderRadius: "8px",
      border: `1px solid ${theme.colors.addovation20Green ?? "#b7d9c8"}`,
      boxShadow: "none !important",
      "&:before": { display: "none" },
      backgroundColor: BULK_ACC_CARD_SURFACE_BG,
    }),
    [theme.colors.addovation20Green],
  );

  const toggleRow = useCallback((key, open) => {
    const fileKey = `file:${String(key)}`;
    if (open) {
      const id = String(key);
      const settings = settingsDraftRef.current;
      setDraftByKey((prev) => {
        const row = prev[id];
        if (!row || row.overridden) {
          return prev;
        }
        const settingsOneDriveDelete = settings.oneDriveDeleteRequired === true;
        const classFormatInSync =
          row.docClass === settings.docClass &&
          row.docFormat === settings.docFormat;
        const rulesInSync =
          row.viewRequired === (settings.viewRequired ?? "N") &&
          row.formatRequired === (settings.formatRequired ?? "N") &&
          row.objConRequired === (settings.objConRequired ?? "N");
        const oneDriveInSync =
          row.oneDriveDeleteOverridden ||
          row.oneDriveDeleteRequired === settingsOneDriveDelete;
        if (classFormatInSync && rulesInSync && oneDriveInSync) {
          return prev;
        }
        return {
          ...prev,
          [id]: {
            ...row,
            docClass: settings.docClass ?? "",
            docFormat: settings.docFormat ?? "",
            viewRequired: settings.viewRequired ?? "N",
            formatRequired: settings.formatRequired ?? "N",
            objConRequired: settings.objConRequired ?? "N",
            ...(row.oneDriveDeleteOverridden
              ? {}
              : { oneDriveDeleteRequired: settingsOneDriveDelete }),
          },
        };
      });
    }
    if (open) {
      setSettingsPanelExpanded(false);
    }
    setActiveKey((prev) => {
      if (open) return fileKey;
      if (prev === fileKey) return INACTIVE_KEY;
      return prev;
    });
  }, []);

  const toggleSettings = useCallback((open) => {
    setSettingsPanelExpanded(open);
    if (open) {
      setActiveKey(SETTINGS_KEY);
    }
  }, []);

  const handleSettingsOneDriveDeleteChange = useCallback((event) => {
    const checked = !!event?.target?.checked;
    setSettingsDraft((prev) =>
      prev.oneDriveDeleteRequired === checked
        ? prev
        : { ...prev, oneDriveDeleteRequired: checked },
    );
    setDraftByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const doc of documentsRef.current) {
        const id = String(doc.key);
        const row = prev[id];
        if (!row || row.oneDriveDeleteOverridden) {
          continue;
        }
        if (row.oneDriveDeleteRequired !== checked) {
          changed = true;
          next[id] = { ...row, oneDriveDeleteRequired: checked };
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const handleFileOneDriveDeleteChange = useCallback((fileKey) => (event) => {
    const checked = !!event?.target?.checked;
    setDraftByKey((prev) => {
      const row = prev[fileKey] ?? {};
      if (
        row.oneDriveDeleteRequired === checked &&
        row.oneDriveDeleteOverridden === true
      ) {
        return prev;
      }
      return {
        ...prev,
        [fileKey]: {
          ...row,
          oneDriveDeleteRequired: checked,
          oneDriveDeleteOverridden: true,
        },
      };
    });
  }, []);

  /** Settings-level rules from get-docformatinfo (class + view copy + object connections). */
  const areSharedBulkRequirementsMet = useMemo(() => {
    if (!settingsDraft.docClass || String(settingsDraft.docClass).trim() === "") {
      return false;
    }
    if (
      isApiFlagYes(settingsDraft.formatRequired) &&
      (!settingsDraft.docFormat || String(settingsDraft.docFormat).trim() === "")
    ) {
      return false;
    }
    if (
      isApiFlagYes(settingsDraft.viewRequired) &&
      settingsDraft.createViewCopy !== true
    ) {
      return false;
    }
    if (
      isApiFlagYes(settingsDraft.objConRequired) &&
      (!Array.isArray(settingsDraft.objectConnections) ||
        settingsDraft.objectConnections.length === 0)
    ) {
      return false;
    }
    return true;
  }, [settingsDraft]);

  const isFileDraftIncomplete = useCallback(
    (draft, doc) => {
      const fileName = doc?.name ?? doc?.title ?? doc?.key ?? "Document";

      if (!draft) {
        //console.log(`[incomplete] ${fileName}: true — no draft`);
        return true;
      }

      const effectiveClass = draft.overridden
        ? draft.docClass
        : draft.docClass || settingsDraft.docClass;
      const effectiveFormat = draft.overridden
        ? draft.docFormat
        : draft.docFormat || settingsDraft.docFormat;
      const formatReqForFile = draft.overridden
        ? isApiFlagYes(draft.formatRequired)
        : isApiFlagYes(settingsDraft.formatRequired);
      const viewReqForFile = resolveBulkViewRequiredFlag(draft, settingsDraft);
      const objReqForFile = draft.overridden
        ? isApiFlagYes(draft.objConRequired)
        : isApiFlagYes(settingsDraft.objConRequired);
      const effectiveObjectConnections = draft.objectConnectionsOverridden
        ? draft.objectConnections
        : settingsDraft.objectConnections;
      const effectiveCreateViewCopy = draft.viewCopyOverridden
        ? draft.createViewCopy === true
        : settingsDraft.createViewCopy === true;
      const effectiveUploadViewCopyCount = getEffectiveViewCopyUploadCount(draft);

      if (!effectiveClass || String(effectiveClass).trim() === "") {
        //console.log(`[incomplete] ${fileName}: true — missing document class`);
        return true;
      }
      if (
        formatReqForFile &&
        (!effectiveFormat || String(effectiveFormat).trim() === "")
      ) {
        //console.log(
        //  `[incomplete] ${fileName}: true — format required but missing`,
        //);
        return true;
      }
      if (
        viewReqForFile &&
        !isViewCopyRequirementMet({
          viewRequired: true,
          uploadViewCopyCount: effectiveUploadViewCopyCount,
          createViewCopy: effectiveCreateViewCopy,
          fileName,
        })
      ) {
        //console.log(
        //  `[incomplete] ${fileName}: true — view copy required but not satisfied`,
        //);
        return true;
      }
      if (
        objReqForFile &&
        (!Array.isArray(effectiveObjectConnections) ||
          effectiveObjectConnections.length === 0)
      ) {
        //console.log(
        //  `[incomplete] ${fileName}: true — object connection required but none selected`,
        //);
        return true;
      }
      if (!draft.docTitle || String(draft.docTitle).trim() === "") {
        //console.log(`[incomplete] ${fileName}: true — missing document title`);
        return true;
      }

      //console.log(`[incomplete] ${fileName}: false — all requirements met`);
      return false;
    },
    [settingsDraft],
  );

  const isBulkFileRowIncomplete = useCallback(
    (draft, doc) => isFileDraftIncomplete(draft, doc),
    [isFileDraftIncomplete],
  );

  const draftsForValidation = useMemo(
    () =>
      mergeActiveFileViewCopyIntoDraftMap(
        draftByKey,
        activeKey,
        viewCopyFiles,
        uploadViewCopyCount,
      ),
    [activeKey, draftByKey, uploadViewCopyCount, viewCopyFiles],
  );

  const allFilesReady = useMemo(() => {
    if (documents.length === 0) {
      return false;
    }
    return documents.every(
      (d) =>
        !isBulkFileRowIncomplete(draftsForValidation[String(d.key)], d),
    );
  }, [documents, draftsForValidation, isBulkFileRowIncomplete]);

  const isCheckInButtonDisabled = useMemo(() => {
    if (
      checkinBulkLoading ||
      isCheckinInLoadingState(docCheckinType, checkinDocumentProgress)
    ) {
      return true;
    }
    if (!allFilesReady) return true;
    if (objectConnectionDataLoad !== DATA_LOAD_STATUS.completed) return true;
    return false;
  }, [
    allFilesReady,
    checkinBulkLoading,
    checkinDocumentProgress,
    docCheckinType,
    objectConnectionDataLoad,
  ]);

  const footerCancelButtonTextStyles = useMemo(
    () => ({
      fontFamily: theme.fonts?.regular,
      fontSize: `${theme.fontSizes?.normal ?? 12}px`,
    }),
    [theme.fonts?.regular, theme.fontSizes?.normal],
  );

  const footerCancelFluentStyles = useMemo(
    () => ({
      root: { marginRight: "10px", ...footerCancelButtonTextStyles },
    }),
    [footerCancelButtonTextStyles],
  );

  const handleFooterCancel = useCallback(() => {
    resetSharedCheckinState();
    navigate(fromRoute);
  }, [fromRoute, navigate, resetSharedCheckinState]);

  const handleFooterCheckIn = useCallback(async () => {
    const incompleteFiles = documents.filter((doc) =>
      isBulkFileRowIncomplete(draftsForValidation[String(doc.key)], doc),
    );
    if (incompleteFiles.length > 0) {
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: `Complete all required fields for every file (${incompleteFiles.length} incomplete).`,
        },
      });
      return;
    }
    if (objectConnectionDataLoad !== DATA_LOAD_STATUS.completed) {
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: "Object connections are still loading. Try again in a moment.",
        },
      });
      return;
    }
    onDocCheckinTypeChange(DOC_CHECK_IN_TYPE.checkinNew);
    onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoad);
    onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoading);

    const draftsForSubmit = mergeActiveFileViewCopyIntoDraftMap(
      draftByKeyRef.current,
      activeKeyRef.current,
      viewCopyFiles,
      uploadViewCopyCount,
    );
    if (draftsForSubmit !== draftByKeyRef.current) {
      draftByKeyRef.current = draftsForSubmit;
      setDraftByKey(draftsForSubmit);
    }

    const inlineViewCopyCount = documents.filter((doc) =>
      draftHasManualViewCopyAttachment(draftsForSubmit[String(doc.key)]),
    ).length;

    const docInfos = [];
    for (const doc of documents) {
      const draft = draftsForSubmit[String(doc.key)];
      const fileUrl = getBulkOneDriveFileUrlFromDoc(doc);
      const oneDriveServerContext = fileUrl
        ? null
        : buildBulkOneDriveServerContext(doc, teamsContext);
      if (!fileUrl && !oneDriveServerContext) {
        onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
        dispatch({
          type: ON_ERROR_MESSAGE_INVOKE,
          payload: {
            type: ERROR_TYPE,
            message: `Could not prepare OneDrive context for "${doc.name ?? doc.title ?? "document"}". Try again.`,
          },
        });
        return;
      }
      docInfos.push(
        buildBulkDocInfo({
          doc,
          draft,
          fileUrl,
          oneDriveServerContext,
          formatRequired:
            draft?.overridden
              ? draft?.formatRequired ?? formatRequired
              : settingsDraft.formatRequired ?? formatRequired,
          viewRequired:
            draft?.overridden
              ? draft?.viewRequired ?? viewRequired
              : settingsDraft.viewRequired ?? viewRequired,
          createViewCopy:
            draft?.viewCopyOverridden
              ? draft?.createViewCopy === true
              : settingsDraft.createViewCopy === true,
          docReleaseStatus:
            draft?.releaseOverridden
              ? draft?.docReleaseStatus ?? ""
              : settingsDraft.docReleaseStatus ?? "",
          selectedObjectConnections: draft?.objectConnectionsOverridden
            ? draft?.objectConnections ?? []
            : settingsDraft.objectConnections ?? [],
          uploadViewCopyCount: draft?.uploadViewCopyCount ?? 0,
        }),
      );
    }

    logger.info(
      "[CheckInNewBulkContent] bulk check-in submit",
      {
        count: docInfos.length,
        inlineViewCopyAttachments: inlineViewCopyCount,
      },
      "DIALOG",
    );

    const action = await dispatch(
      checkinDocumentsBulkThunk({
        documents: docInfos,
        __toast: { suppressToast: true },
      }),
    );

    if (!checkinDocumentsBulkThunk.fulfilled.match(action)) {
      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
      return;
    }

    const bulk = action.payload?.data ?? {};
    const successCount = bulk.successCount ?? 0;
    const failureCount = bulk.failureCount ?? 0;

    if (successCount === 0) {
      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
      const firstError =
        bulk.results?.[0]?.errorMessage ??
        "Bulk check-in failed for all documents.";
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          title: "Bulk check-in failed",
          message: `${failureCount} failed. ${firstError}`,
        },
      });
      return;
    }

    const { groupId: deleteGroupId, channelId, fileRoot: deleteFileRoot } =
      resolveGroupId(teamsContext);
    const deleteMembershipType =
      teamsContext?.channel?.membershipType || "Regular";
    const oneDriveDeleteIds = collectBulkOneDriveDeleteIds(
      documents,
      draftsForSubmit,
      settingsDraft,
      bulk,
      docInfos,
    );

    if (oneDriveDeleteIds.length > 0) {
      try {
        await deleteOneDriveDocumentsBulk(
          deleteMembershipType,
          deleteGroupId || "",
          channelId ?? "",
          teamsContext?.user?.tenant?.id ?? "",
          deleteFileRoot,
          oneDriveDeleteIds,
        );
      } catch (_e) {
        // Deletion failure should not block the check-in success flow.
      }
    }

    dispatch({
      type: ON_SUCCESS_MESSAGE_INVOKE,
      payload: {
        type: SUCCESS_TYPE,
        title: getBulkCheckinToastTitle(failureCount),
        message: formatBulkCheckinToastMessage(successCount, failureCount),
      },
    });

    finalizeBulkCheckinSuccess();
  }, [
    deleteOneDriveDocumentsBulk,
    dispatch,
    documents,
    draftByKey,
    draftsForValidation,
    finalizeBulkCheckinSuccess,
    formatRequired,
    isBulkFileRowIncomplete,
    objConRequired,
    objectConnectionDataLoad,
    onCheckinDocumentProgress,
    onDocCheckinTypeChange,
    settingsDraft,
    teamsContext,
    uploadViewCopyCount,
    viewCopyFiles,
    viewRequired,
  ]);

  if (!documents.length) {
    return null;
  }

  return (
    <Stack
      styles={{
        root: {
          width: "100%",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {showCheckinProgressDialog && (
        <ReusableDialog
          isOpen
          onClose={() => {}}
          showHeader={false}
          isBlocking
          centerContent
          dialogMinHeight={100}
          dialogMinWidth="300px"
          bodyMinHeight={60}
        >
          <MessageBoxContent
            isLoading
            loadingText={CHECKIN_LOADING_MESSAGE}
            showOkButton={false}
          />
        </ReusableDialog>
      )}
      <Stack.Item disableShrink>
        <Accordion
          elevation={0}
          expanded={activeKey === SETTINGS_KEY && settingsPanelExpanded}
          onChange={(_e, expanded) => toggleSettings(Boolean(expanded))}
          sx={accordionSettingsOuter}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
            sx={{
              ...BULK_ACC_HEADER_SUMMARY_SX,
              px: 2,
              "& .MuiAccordionSummary-content": {
                ...BULK_ACC_HEADER_SUMMARY_SX["& .MuiAccordionSummary-content"],
                gap: "8px",
              },
            }}
          >
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <TaskListSquareSettingsRegular
                aria-hidden
                primaryFill="#237b4b"
                style={{ width: 22, height: 22, flexShrink: 0 }}
              />
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#2d6e4f",
                  fontFamily: theme.fonts.regular,
                  fontWeight: 600,
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                }}
              >
                Settings
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: 2,
              pb: 2,
              pt: 0,
              backgroundColor: theme.colors.backgroundColor,
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {activeKey === SETTINGS_KEY && (
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                <DocumentBasicItem
                  key="settings"
                  omitTitleSection
                  omitOneDriveDeletionRow
                  bulkSettingsViewCopy
                  onTreeDataLoadingChange={setIsTreeDataLoading}
                  objectConnectionTabKey={objectConnectionTabKey}
                  onObjectConnectionTabKeyChange={setObjectConnectionTabKey}
                  tabKey={tabKey}
                />
                <DeleteOneDriveCheckbox
                  checked={settingsDraft.oneDriveDeleteRequired === true}
                  onChange={handleSettingsOneDriveDeleteChange}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Stack.Item>

      <Stack.Item
        disableShrink
        styles={{ root: { marginTop: 20, width: "100%" } }}
      >
        <Text
          styles={{
            root: {
              fontWeight: 600,
              fontFamily: theme.fonts.regular,
              fontSize: theme.fontSizes?.normal ?? 12,
              color: theme.colors.fontColor,
            },
          }}
        >
          File(s)
        </Text>
      </Stack.Item>

      <Stack.Item disableShrink styles={{ root: { width: "100%" } }}>
        <Box sx={{ pb: "24px", width: "100%" }}>
          {documents.map((doc) => {
            const id = String(doc.key);
            const draft = draftsForValidation[id] ?? draftByKey[id];
            return (
              <BulkCheckInFileRow
                key={id}
                doc={doc}
                expanded={activeKey === `file:${id}`}
                incomplete={isBulkFileRowIncomplete(draft, doc)}
                onToggleExpand={toggleRow}
                onTreeDataLoadingChange={setIsTreeDataLoading}
                objectConnectionTabKey={objectConnectionTabKey}
                onObjectConnectionTabKeyChange={setObjectConnectionTabKey}
                tabKey={tabKey}
                oneDriveDeleteChecked={resolveOneDriveDeleteRequired(
                  draft,
                  settingsDraft,
                )}
                onOneDriveDeleteChange={handleFileOneDriveDeleteChange(id)}
              />
            );
          })}
        </Box>
      </Stack.Item>

      <Stack.Item disableShrink styles={{ root: { width: "100%" } }}>
        <Stack
          horizontal
          horizontalAlign="center"
          verticalAlign="center"
          tokens={{ childrenGap: 12 }}
          styles={{
            root: {
              width: "100%",
              paddingTop: 24,
              paddingBottom: 16,
              flexWrap: "wrap",
              justifyContent: "center",
            },
          }}
        >
          <div style={{ marginRight: "10px" }}>
            <PrimaryButton
              styles={footerCancelFluentStyles}
              style={styleTheme.CancelButtonFillStyle}
              id="btncancel-bulk"
              text="Cancel"
              ariaLabel="Cancel and return to list"
              onClick={handleFooterCancel}
            />
          </div>
          <PrimaryButton
            style={
              isCheckInButtonDisabled
                ? styleTheme.DisableButtoneStyle
                : styleTheme.PrimaryButtonFillStyle
            }
            text={showCheckinProgressDialog ? "Checking in…" : "Check-in"}
            ariaLabel="Submit bulk check-in"
            disabled={isCheckInButtonDisabled}
            onClick={handleFooterCheckIn}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
}
